import type { Config } from 'tailwindcss';
import defaultTheme from 'tailwindcss/defaultTheme';

import { colors, fontSizes } from './src/styles/types';

export default {
  content: ['./src/**/*.{js,jsx,ts,tsx}'],
  safelist: [
    ...[
      'before:',
      'before:!',
      'max-md:before:',
      'max-md:before:!',
      'dark:before:',
      'dark:before:!',
      'max-md:dark:before:',
      'max-md:dark:before:!',
      'dark:hover:',
      'dark:hover:!',
      'dark:hover:before:',
      'dark:hover:before:!',
      'dark:',
      'dark:!',
      'max-md:dark:',
      'max-md:dark:!',
      '',
    ]
      .map((prefix) => {
        const classesWithPrefixes = [
          'rounded',
          'rounded-xl',
          'rounded-3xl',
          'rounded-[2.25rem]',
          'p-px',
          'p-1',
          'p-0.5',
          'border-gradient',
          'overflow-visible',
          'border-gradient-base',
          'border-gradient-main',
          'border-gradient-main-90deg',
          'bg-gradient-main',
          'bg-gradient-main-90deg',
          'border-gradient-secondary-1',
          'bg-gradient-secondary-1',
          'border-gradient-secondary-2',
          'bg-gradient-secondary-2',
          'border-gradient-secondary-3',
          'bg-gradient-secondary-3',
          'border-gradient-secondary-4',
          'bg-gradient-secondary-4',
          'bg-gradient-secondary-5',
        ].map((classVar) => `${prefix}${classVar}`);
        return classesWithPrefixes;
      })
      .flat(),
    ...colors.map((color) => `bg-${color}`),
    ...colors.map((color) => `border-${color}`),
    ...colors.map((color) => `text-${color}`),
    ...fontSizes.map((fontSize) => `text-${fontSize}`),
  ],
  darkMode: ['class'],
  theme: {
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      inherit: 'inherit',
      neutral: {
        N00: 'rgb(var(--color-neutral-N00), <alpha-value>)',
        N02: 'rgb(var(--color-neutral-N02), <alpha-value>)',
        N04: 'rgb(var(--color-neutral-N04), <alpha-value>)',
        N10: 'rgb(var(--color-neutral-N10), <alpha-value>)',
        N20: 'rgb(var(--color-neutral-N20), <alpha-value>)',
        N40: 'rgb(var(--color-neutral-N40), <alpha-value>)',
        N50: 'rgb(var(--color-neutral-N50), <alpha-value>)',
        N80: 'rgb(var(--color-neutral-N80), <alpha-value>)',
        N90: 'rgb(var(--color-neutral-N90), <alpha-value>)',
        N94: 'rgb(var(--color-neutral-N94), <alpha-value>)',
        N99: 'rgb(var(--color-neutral-N99), <alpha-value>)',
        dark: {
          N00: 'rgb(var(--color-neutral-dark-N00), <alpha-value>)',
          N02: 'rgb(var(--color-neutral-dark-N02), <alpha-value>)',
          N04: 'rgb(var(--color-neutral-dark-N04), <alpha-value>)',
          N10: 'rgb(var(--color-neutral-dark-N10), <alpha-value>)',
          N20: 'rgb(var(--color-neutral-dark-N20), <alpha-value>)',
          N40: 'rgb(var(--color-neutral-dark-N40), <alpha-value>)',
          N50: 'rgb(var(--color-neutral-dark-N50), <alpha-value>)',
          N80: 'rgb(var(--color-neutral-dark-N80), <alpha-value>)',
          N90: 'rgb(var(--color-neutral-dark-N90), <alpha-value>)',
          N94: 'rgb(var(--color-neutral-dark-N94), <alpha-value>)',
          N99: 'rgb(var(--color-neutral-dark-N99), <alpha-value>)',
        },
      },
      brand: {
        B06: 'rgb(var(--color-brand-B06), <alpha-value>)',
        B50: 'rgb(var(--color-brand-B50), <alpha-value>)',
        B99: 'rgb(var(--color-brand-B99), <alpha-value>)',
        // decodo
        25: 'rgb(var(--color-brand-25), <alpha-value>)',
        50: 'rgb(var(--color-brand-50), <alpha-value>)',
        100: 'rgb(var(--color-brand-100), <alpha-value>)',
        200: 'rgb(var(--color-brand-200), <alpha-value>)',
        300: 'rgb(var(--color-brand-300), <alpha-value>)',
        400: 'rgb(var(--color-brand-400), <alpha-value>)',
        500: 'rgb(var(--color-brand-500), <alpha-value>)',
        600: 'rgb(var(--color-brand-600), <alpha-value>)',
        700: 'rgb(var(--color-brand-700), <alpha-value>)',
        800: 'rgb(var(--color-brand-800), <alpha-value>)',
        900: 'rgb(var(--color-brand-900), <alpha-value>)',
        950: 'rgb(var(--color-brand-950), <alpha-value>)',
        975: 'rgb(var(--color-brand-975), <alpha-value>)',
      },
      scraping: {
        S06: 'rgb(var(--color-scraping-S06), <alpha-value>)',
        S50: 'rgb(var(--color-scraping-S50), <alpha-value>)',
        S99: 'rgb(var(--color-scraping-S99), <alpha-value>)',
      },
      primary: {
        P15: 'rgb(var(--color-primary-P15), <alpha-value>)',
        P50: 'rgb(var(--color-primary-P50), <alpha-value>)',
        P99: 'rgb(var(--color-primary-P99), <alpha-value>)',
      },
      success: {
        S06: 'rgb(var(--color-success-S06), <alpha-value>)',
        S50: 'rgb(var(--color-success-S50), <alpha-value>)',
        S99: 'rgb(var(--color-success-S99), <alpha-value>)',
        25: 'rgb(var(--color-success-25), <alpha-value>)',
        50: 'rgb(var(--color-success-50), <alpha-value>)',
        100: 'rgb(var(--color-success-100), <alpha-value>)',
        200: 'rgb(var(--color-success-200), <alpha-value>)',
        300: 'rgb(var(--color-success-300), <alpha-value>)',
        400: 'rgb(var(--color-success-400), <alpha-value>)',
        500: 'rgb(var(--color-success-500), <alpha-value>)',
        600: 'rgb(var(--color-success-600), <alpha-value>)',
        700: 'rgb(var(--color-success-700), <alpha-value>)',
        800: 'rgb(var(--color-success-800), <alpha-value>)',
        900: 'rgb(var(--color-success-900), <alpha-value>)',
        950: 'rgb(var(--color-success-950), <alpha-value>)',
      },
      error: {
        E06: 'rgb(var(--color-error-E06), <alpha-value>)',
        E50: 'rgb(var(--color-error-E50), <alpha-value>)',
        E99: 'rgb(var(--color-error-E99), <alpha-value>)',
        // decodo
        25: 'rgb(var(--color-error-25), <alpha-value>)',
        50: 'rgb(var(--color-error-50), <alpha-value>)',
        100: 'rgb(var(--color-error-100), <alpha-value>)',
        200: 'rgb(var(--color-error-200), <alpha-value>)',
        300: 'rgb(var(--color-error-300), <alpha-value>)',
        400: 'rgb(var(--color-error-400), <alpha-value>)',
        500: 'rgb(var(--color-error-500), <alpha-value>)',
        600: 'rgb(var(--color-error-600), <alpha-value>)',
        700: 'rgb(var(--color-error-700), <alpha-value>)',
        800: 'rgb(var(--color-error-800), <alpha-value>)',
        900: 'rgb(var(--color-error-900), <alpha-value>)',
        950: 'rgb(var(--color-error-950), <alpha-value>)',
      },
      warning: {
        W06: 'rgb(var(--color-warning-W06), <alpha-value>)',
        W50: 'rgb(var(--color-warning-W50), <alpha-value>)',
        W99: 'rgb(var(--color-warning-W99), <alpha-value>)',
        25: 'rgb(var(--color-warning-25), <alpha-value>)',
        50: 'rgb(var(--color-warning-50), <alpha-value>)',
        100: 'rgb(var(--color-warning-100), <alpha-value>)',
        200: 'rgb(var(--color-warning-200), <alpha-value>)',
        300: 'rgb(var(--color-warning-300), <alpha-value>)',
        400: 'rgb(var(--color-warning-400), <alpha-value>)',
        500: 'rgb(var(--color-warning-500), <alpha-value>)',
        600: 'rgb(var(--color-warning-600), <alpha-value>)',
        700: 'rgb(var(--color-warning-700), <alpha-value>)',
        800: 'rgb(var(--color-warning-800), <alpha-value>)',
        900: 'rgb(var(--color-warning-900), <alpha-value>)',
        950: 'rgb(var(--color-warning-950), <alpha-value>)',
      },
      info: {
        I99: 'rgb(var(--color-info-I99), <alpha-value>)',
      },
      gradient: {
        1: 'rgb(var(--color-gradient-1), <alpha-value>)',
        2: 'rgb(var(--color-gradient-2), <alpha-value>)',
        3: 'rgb(var(--color-gradient-3), <alpha-value>)',
      },
      // decodo
      gray: {
        25: 'rgb(var(--color-gray-25), <alpha-value>)',
        50: 'rgb(var(--color-gray-50), <alpha-value>)',
        100: 'rgb(var(--color-gray-100), <alpha-value>)',
        200: 'rgb(var(--color-gray-200), <alpha-value>)',
        300: 'rgb(var(--color-gray-300), <alpha-value>)',
        400: 'rgb(var(--color-gray-400), <alpha-value>)',
        500: 'rgb(var(--color-gray-500), <alpha-value>)',
        600: 'rgb(var(--color-gray-600), <alpha-value>)',
        700: 'rgb(var(--color-gray-700), <alpha-value>)',
        800: 'rgb(var(--color-gray-800), <alpha-value>)',
        900: 'rgb(var(--color-gray-900), <alpha-value>)',
        950: 'rgb(var(--color-gray-950), <alpha-value>)',
        dark: {
          25: 'rgb(var(--color-gray-dark-25), <alpha-value>)',
          50: 'rgb(var(--color-gray-dark-50), <alpha-value>)',
          100: 'rgb(var(--color-gray-dark-100), <alpha-value>)',
          200: 'rgb(var(--color-gray-dark-200), <alpha-value>)',
          300: 'rgb(var(--color-gray-dark-300), <alpha-value>)',
          400: 'rgb(var(--color-gray-dark-400), <alpha-value>)',
          500: 'rgb(var(--color-gray-dark-500), <alpha-value>)',
          600: 'rgb(var(--color-gray-dark-600), <alpha-value>)',
          700: 'rgb(var(--color-gray-dark-700), <alpha-value>)',
          800: 'rgb(var(--color-gray-dark-800), <alpha-value>)',
          900: 'rgb(var(--color-gray-dark-900), <alpha-value>)',
          950: 'rgb(var(--color-gray-dark-950), <alpha-value>)',
        },
      },
      secondary: {
        25: 'rgb(var(--color-secondary-25), <alpha-value>)',
        50: 'rgb(var(--color-secondary-50), <alpha-value>)',
        100: 'rgb(var(--color-secondary-100), <alpha-value>)',
        200: 'rgb(var(--color-secondary-200), <alpha-value>)',
        300: 'rgb(var(--color-secondary-300), <alpha-value>)',
        400: 'rgb(var(--color-secondary-400), <alpha-value>)',
        500: 'rgb(var(--color-secondary-500), <alpha-value>)',
        600: 'rgb(var(--color-secondary-600), <alpha-value>)',
        700: 'rgb(var(--color-secondary-700), <alpha-value>)',
        800: 'rgb(var(--color-secondary-800), <alpha-value>)',
        900: 'rgb(var(--color-secondary-900), <alpha-value>)',
        950: 'rgb(var(--color-secondary-950), <alpha-value>)',
      },
      tertiary: {
        25: 'rgb(var(--color-tertiary-25), <alpha-value>)',
        50: 'rgb(var(--color-tertiary-50), <alpha-value>)',
        100: 'rgb(var(--color-tertiary-100), <alpha-value>)',
        200: 'rgb(var(--color-tertiary-200), <alpha-value>)',
        300: 'rgb(var(--color-tertiary-300), <alpha-value>)',
        400: 'rgb(var(--color-tertiary-400), <alpha-value>)',
        500: 'rgb(var(--color-tertiary-500), <alpha-value>)',
        600: 'rgb(var(--color-tertiary-600), <alpha-value>)',
        700: 'rgb(var(--color-tertiary-700), <alpha-value>)',
        800: 'rgb(var(--color-tertiary-800), <alpha-value>)',
        900: 'rgb(var(--color-tertiary-900), <alpha-value>)',
        950: 'rgb(var(--color-tertiary-950), <alpha-value>)',
      },
      base: {
        white: 'rgb(var(--color-base-white), <alpha-value>)',
        black: 'rgb(var(--color-base-black), <alpha-value>)',
        dark: {
          white: 'rgb(var(--color-base-dark-white), <alpha-value>)',
          black: 'rgb(var(--color-base-dark-black), <alpha-value>)',
        },
      },
    },
    screens: {
      sm: '480px',
      // TODO: change md to 960 and remove m: on release
      md: '768px',
      m: '960px', // TODO: change value for Decodo inside useScreenSize hook
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
      headerLg: '1340px',
      desktop: '1440px',
      'hover-hover': { raw: '(hover: hover)' },
    },
    fontSize: {
      xl: [
        '6rem',
        { lineHeight: '9rem', letterSpacing: '0em', fontWeight: '600' },
      ],
      l: [
        '4rem',
        { lineHeight: '5.5rem', letterSpacing: '0em', fontWeight: '600' },
      ],
      h1: [
        '2.25rem',
        { lineHeight: '3.375rem', letterSpacing: '0em', fontWeight: '600' },
      ],
      h2: [
        '2rem',
        { lineHeight: '3rem', letterSpacing: '0em', fontWeight: '600' },
      ],
      h3: [
        '1.5rem',
        { lineHeight: '2.25rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      h4: [
        '1.125rem',
        { lineHeight: '1.875rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      h5: [
        '1rem',
        { lineHeight: '1.75rem', letterSpacing: '0em', fontWeight: '500' },
      ],

      ['subtitle-l']: [
        '1.25rem',
        { lineHeight: '1.5rem', letterSpacing: '0em', fontWeight: '700' },
      ],
      // start decodo
      ['subtitle-l-d']: [
        '1.25rem',
        { lineHeight: '1.875rem', letterSpacing: '0em', fontWeight: '600' },
      ],
      // end decodo
      ['subtitle-m']: [
        '1.125rem',
        { lineHeight: '1.25rem', letterSpacing: '0em', fontWeight: '700' },
      ],

      ['blog-body']: [
        '1.25rem',
        { lineHeight: '1.875rem', letterSpacing: '0em', fontWeight: '400' },
      ],

      ['body-l']: [
        '1rem',
        { lineHeight: '1.5rem', letterSpacing: '0em', fontWeight: '400' },
      ],
      // start decodo
      ['body-l-medium']: [
        '1rem',
        { lineHeight: '1.5rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      ['body-l-bold']: [
        '1rem',
        { lineHeight: '1.5rem', letterSpacing: '0em', fontWeight: '700' },
      ],
      // end decodo

      ['body-m']: [
        '0.875rem',
        { lineHeight: '1.25rem', letterSpacing: '0em', fontWeight: '400' },
      ],
      // start decodo
      ['body-m-medium']: [
        '0.875rem',
        { lineHeight: '1.25rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      ['body-m-bold']: [
        '0.875rem',
        { lineHeight: '1.25rem', letterSpacing: '0em', fontWeight: '700' },
      ],
      // end decodo

      ['body-s']: [
        '0.75rem',
        { lineHeight: '1.25rem', letterSpacing: '0em', fontWeight: '400' },
      ],
      // start decodo
      ['body-s-medium']: [
        '0.75rem',
        { lineHeight: '1.125rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      ['body-s-bold']: [
        '0.75rem',
        { lineHeight: '1.125rem', letterSpacing: '0em', fontWeight: '700' },
      ],
      // end decodo

      ['button-text-l']: [
        '1.125rem',
        { lineHeight: '1.67rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      ['button-text-m']: [
        '1rem',
        { lineHeight: '1.5rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      ['button-text-s']: [
        '0.875rem',
        { lineHeight: '1.43rem', letterSpacing: '0em', fontWeight: '500' },
      ],
      ['label-text-l']: [
        '0.75rem',
        { lineHeight: '1rem', letterSpacing: '0.1em', fontWeight: '400' },
      ],
      ['label-text-m']: [
        '0.625rem',
        { lineHeight: '0.75rem', letterSpacing: '0.1em', fontWeight: '400' },
      ],
      // start decodo
      ['label']: [
        '0.75rem',
        { lineHeight: '1rem', letterSpacing: '0.1em', fontWeight: '400' },
      ],
      ['label-medium']: [
        '0.75rem',
        { lineHeight: '1rem', letterSpacing: '0.1em', fontWeight: '600' },
      ],

      ['label-s']: [
        '0.625rem',
        { lineHeight: '0.75rem', letterSpacing: '0.1em', fontWeight: '400' },
      ],
      ['label-s-medium']: [
        '0.625rem',
        { lineHeight: '0.75rem', letterSpacing: '0.1em', fontWeight: '600' },
      ],
      // end decodo
      ['code-sample']: [
        '1rem',
        { lineHeight: '1.25rem', letterSpacing: '0em', fontWeight: '400' },
      ],
    },
    fontFamily: {
      sans: ['var(--font-dm-sans)', ...defaultTheme.fontFamily.sans],
      serif: ['var(--font-poppins)', ...defaultTheme.fontFamily.serif],
      mono: ['var(--font-roboto)', ...defaultTheme.fontFamily.mono],
      code: ['var(--font-fira-code)', ...defaultTheme.fontFamily.mono],
    },

    extend: {
      boxShadow: {
        card: '0px 4px 16px 0px rgba(var(--color-neutral-N94), 0.08)',
        popup: '0px 6px 18px 0px rgba(var(--color-neutral-N94), 0.16)',
        tiny: '0px 1px 6px 0px rgba(var(--color-neutral-N99), 0.12)',
        'border-px': 'inset 0px 0px 0px 1px',
        image: '0px 4px 8px rgba(29, 29, 31, 0.1)',
        // start decodo
        'card-d': '0px 4px 16px 0px rgba(29, 29, 31, 0.16)',
        'popup-d': '0px 6px 18px 0px rgba(29, 29, 31, 0.16)',
        'tiny-d': '0px 2px 6px 0px rgba(13, 15, 16, 0.12)',
      },
      keyframes: {
        ['zoom-in']: {
          '0%': { transform: 'scale(0)' },
          '100%': { transform: 'scale(1)' },
        },
        'slide-bottom-top': {
          from: { transform: 'translateY(0)' },
          to: { transform: 'translateY(-100%)' },
        },
        'slide-top-bottom': {
          from: { transform: 'translateY(-100%)' },
          to: { transform: 'translateY(0)' },
        },
      },
      animation: {
        ['zoom-in']: 'zoom-in 0.5s ease-in-out forwards',
        'slide-bottom-top': 'slide-bottom-top linear infinite 20s',
        'slide-top-bottom': 'slide-top-bottom linear infinite 20s',
      },
      backgroundImage: {
        'gradient-main':
          'linear-gradient(135deg, rgba(var(--color-success-500), 1) 0%, rgba(var(--color-tertiary-500), 1) 20%, rgba(var(--color-brand-500), 1) 40%, rgba(var(--color-secondary-500), 1) 60%, rgba(var(--color-error-500), 1) 80%, rgba(var(--color-warning-500), 1) 100%)',
        'gradient-main-90deg':
          'linear-gradient(90deg, rgba(var(--color-success-500), 1) 0%, rgba(var(--color-tertiary-500), 1) 20%, rgba(var(--color-brand-500), 1) 40%, rgba(var(--color-secondary-500), 1) 60%, rgba(var(--color-error-500), 1) 80%, rgba(var(--color-warning-500), 1) 100%)',
        'gradient-secondary-1':
          'linear-gradient(135deg, rgba(var(--color-brand-400), 1) 0%, rgba(var(--color-brand-500), 1) 50%, rgba(var(--color-secondary-500), 1) 100%)',
        'gradient-secondary-2':
          'linear-gradient(135deg, rgba(var(--color-secondary-400), 1) 0%, rgba(var(--color-secondary-500), 1) 50%, rgba(var(--color-error-500), 1) 100%)',
        'gradient-secondary-3':
          'linear-gradient(135deg, rgba(var(--color-tertiary-400), 1) 0%, rgba(var(--color-tertiary-500), 1) 50%, rgba(var(--color-brand-500), 1) 100%)',
        'gradient-secondary-4':
          'linear-gradient(135deg, rgba(var(--color-success-400), 1) 0%, rgba(var(--color-success-500), 1) 50%, rgba(var(--color-tertiary-500), 1) 100%)',
        'gradient-secondary-5':
          /* This is gradient-secondary-1 but with 40% opacity. Option added as opacity does not apply to gradients directly. */
          'linear-gradient(135deg, rgba(var(--color-brand-400), 0.4) 0%, rgba(var(--color-brand-500), 0.4) 40%, rgba(var(--color-secondary-500), 0.4) 100%)',
      },
      borderColor: {
        // just for intellisense. CSS is in src/styles/globals.css and usage in src/styles/gradient.ts
        'gradient-main': '',
        'gradient-main-90deg': '',
        'gradient-secondary-1': '',
        'gradient-secondary-2': '',
        'gradient-secondary-3': '',
        'gradient-secondary-4': '',
      },

      // decodo-only
      maxWidth: {
        'section-full': '1440px',
        'section-mid': '1360px',
        'section-contained': '1024px',
      },
    },
  },
} satisfies Config;
